'use strict';

var debounce = require('lodash/debounce');

var UP_KEY = 38;
var DOWN_KEY = 40;
var DIRECTION_DOWN = 1;
var DIRECTION_UP = -1;

/**
 * Returns whether the blog search field has a valid query.
 * @param {Object} scope - search input element
 * @returns {boolean} true when query is not empty
 */
function hasSearchQuery(scope) {
    return $(scope).val().trim().length > 0;
}

/**
 * Enables or disables the blog search submit button based on input value.
 * @param {Object} scope - search input element
 */
function updateSearchButtonState(scope) {
    var $form = getSearchForm(scope);
    var isEnabled = hasSearchQuery(scope);

    $form.find('.blog-search-button')
        .prop('disabled', !isEnabled)
        .attr('aria-disabled', !isEnabled);
}

/**
 * Returns the suggestions wrapper for a blog search field.
 * @param {Object} scope - search input element
 * @returns {JQuery} suggestions wrapper
 */
function getSuggestionsWrapper(scope) {
    return $(scope).closest('.blog-search-form-wrap').find('.blog-suggestions-wrapper');
}

/**
 * Returns the blog search form for a search field.
 * @param {Object} scope - search input element
 * @returns {JQuery} search form
 */
function getSearchForm(scope) {
    return $(scope).closest('.blog-search-form');
}

/**
 * Returns suggestion option items for the active search field.
 * @param {Object} scope - search input element
 * @returns {JQuery} suggestion items
 */
function getSuggestionItems(scope) {
    return getSuggestionsWrapper(scope).find('.blog-suggestions .item');
}

/**
 * Updates aria-activedescendant on the search field from the selected option.
 * @param {Object} scope - search input element
 * @param {JQuery|null} $selected - selected suggestion item
 */
function setActiveDescendant(scope, $selected) {
    var $field = $(scope);

    if ($selected && $selected.length && $selected.attr('id')) {
        $field.attr('aria-activedescendant', $selected.attr('id'));
        return;
    }

    $field.removeAttr('aria-activedescendant');
}

/**
 * Clears suggestion selection state for a search field.
 * @param {Object} scope - search input element
 */
function clearActiveSuggestion(scope) {
    getSuggestionItems(scope)
        .removeClass('selected')
        .attr('aria-selected', false);
    setActiveDescendant(scope, null);
}

/**
 * Shows the inline blog search loader.
 * @param {Object} scope - search input element
 */
function showLoader(scope) {
    var $form = getSearchForm(scope);

    if (!$form.find('.blog-search-loader').length) {
        $form.append(
            '<span class="blog-search-loader" aria-hidden="true">'
            + '<span class="blog-search-loader-bar"></span>'
            + '<span class="blog-search-loader-bar"></span>'
            + '</span>'
        );
    }

    $form.addClass('is-loading');
    $(scope).attr('aria-busy', 'true');
}

/**
 * Hides the inline blog search loader.
 * @param {Object} scope - search input element
 */
function hideLoader(scope) {
    getSearchForm(scope).removeClass('is-loading').find('.blog-search-loader').remove();
    $(scope).attr('aria-busy', 'false');
}

/**
 * Handles keyboard navigation within suggestions for one search field.
 * @param {Object} scope - search input element
 * @param {number} direction - navigation direction
 */
function handleArrow(scope, direction) {
    var suggestionsList = getSuggestionItems(scope);
    var $selected;
    var $next;
    var idx;

    if (!suggestionsList.length) {
        return;
    }

    $selected = suggestionsList.filter('.selected');

    if ($selected.length === 0) {
        $next = suggestionsList.first();
        $next.addClass('selected').attr('aria-selected', true);
        setActiveDescendant(scope, $next);
        return;
    }

    idx = suggestionsList.index($selected);
    $selected.removeClass('selected').attr('aria-selected', false);
    idx += direction;

    if (idx >= 0 && idx < suggestionsList.length) {
        $next = suggestionsList.eq(idx);
    } else if (direction === DIRECTION_DOWN) {
        $next = suggestionsList.first();
    } else {
        $next = suggestionsList.last();
    }

    $next.addClass('selected').attr('aria-selected', true);
    setActiveDescendant(scope, $next);
}

/**
 * Toggles the mobile search card elevation while suggestions are open.
 * @param {Object} scope - search input element
 * @param {boolean} isOpen - whether suggestions are visible
 */
function setSuggestionsOpen(scope, isOpen) {
    $(scope).closest('.blog-search-card').toggleClass('is-suggestions-open', isOpen);
}

/**
 * Processes suggestion AJAX response.
 * @param {Object|string} response - rendered suggestions HTML or empty object
 */
function processResponse(response) {
    hideLoader(this);

    var $wrapper = getSuggestionsWrapper(this).empty();
    var $field = $(this);

    if (typeof response !== 'object') {
        $wrapper.append(response).show();
        getSearchForm(this).addClass('has-suggestions');
        $field.attr('aria-expanded', 'true');
        clearActiveSuggestion(this);
        setSuggestionsOpen(this, true);
        return;
    }

    getSearchForm(this).removeClass('has-suggestions');
    $wrapper.hide();
    $field.attr('aria-expanded', 'false');
    clearActiveSuggestion(this);
    setSuggestionsOpen(this, false);
}

/**
 * Fetches blog search suggestions.
 * @param {Object} scope - search input element
 */
function getSuggestions(scope) {
    var $scope = $(scope);
    var $wrapper = getSuggestionsWrapper(scope);
    var endpoint = $wrapper.data('url');
    var minChars = parseInt($wrapper.data('minChars'), 10) || 3;
    var query = $scope.val().trim();

    if (!endpoint) {
        return;
    }

    if (query.length >= minChars) {
        showLoader(scope);
        $.ajax({
            context: scope,
            url: endpoint + encodeURIComponent(query),
            method: 'GET',
            success: processResponse,
            error: function () {
                hideLoader(scope);
                getSearchForm(scope).removeClass('has-suggestions');
                $wrapper.empty().hide();
                $scope.attr('aria-expanded', 'false');
                clearActiveSuggestion(scope);
                setSuggestionsOpen(scope, false);
            }
        });
    } else {
        hideLoader(scope);
        getSearchForm(scope).removeClass('has-suggestions');
        $wrapper.empty().hide();
        $scope.attr('aria-expanded', 'false');
        clearActiveSuggestion(scope);
        setSuggestionsOpen(scope, false);
    }
}

module.exports = function () {
    var debounceSuggestions = debounce(getSuggestions, 300);

    $('.blog-search-field').each(function () {
        updateSearchButtonState(this);
    });

    $('body').on('input', '.blog-search-field', function () {
        updateSearchButtonState(this);
    });

    $('body').on('submit', '.blog-search-form', function (e) {
        var $field = $(this).find('.blog-search-field');

        if (!hasSearchQuery($field[0])) {
            e.preventDefault();
            $field.focus();
        }
    });

    $('body').on('click', '.blog-search-button', function (e) {
        var $form = $(this).closest('form');
        var $field = $form.find('.blog-search-field');

        if (!hasSearchQuery($field[0])) {
            e.preventDefault();
            $field.focus();
            return;
        }

        e.preventDefault();
        $form.trigger('submit');
    });

    $('body').on('keyup focus', '.blog-search-field', function (e) {
        debounceSuggestions(this, e);

        switch (e.which) {
            case DOWN_KEY:
                e.preventDefault();
                handleArrow(this, DIRECTION_DOWN);
                break;
            case UP_KEY:
                e.preventDefault();
                handleArrow(this, DIRECTION_UP);
                break;
            default:
                break;
        }
    });

    $('body').on('keydown', '.blog-search-field', function (e) {
        var $selected;

        if (e.which === 13) {
            if (!hasSearchQuery(this)) {
                e.preventDefault();
                return;
            }

            $selected = getSuggestionItems(this).filter('.selected').find('a');
            if ($selected.length) {
                e.preventDefault();
                window.location.href = $selected.attr('href');
            }
        }
    });

    $('body').on('click', function (e) {
        if (!$(e.target).closest('.blog-search-form-wrap').length) {
            $('.blog-suggestions-wrapper').empty().hide();
            $('.blog-search-form').removeClass('has-suggestions is-loading').find('.blog-search-loader').remove();
            $('.blog-search-field')
                .attr('aria-expanded', 'false')
                .attr('aria-busy', 'false')
                .removeAttr('aria-activedescendant');
            $('.blog-search-card').removeClass('is-suggestions-open');
        }
    });
};

$(document).ready(function () {
    module.exports();
});
